/**
 * Create a staff account.
 *
 * Staff only. A client does not get accounts: it has one portal login, set on
 * its own page under Portal access.
 *
 * Username and email are both required, and either one signs in. A sign-in
 * mobile is optional: it signs in too, and is where a texted code goes. It is
 * not the contact mobile on the people register, which never signs anybody in.
 *
 * **The password is shown once.** It is generated by the server, returned in
 * that one response, and stored nowhere — so this dialog ends on a panel that
 * says so and offers a copy button, rather than closing on a toast that would
 * take the only copy with it.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Check, Copy, KeyRound } from 'lucide-react';
import { ApiError, Alert, Button, Input, Modal, RecordSelect, api, toast } from '@connect/web-shared';

import { RoleAssignmentEditor, type Assignment } from './role-assignment-editor';

/** The server's rule, repeated so a typo shows before the request. */
const USERNAME = /^[a-z][a-z0-9._-]{2,63}$/;

const schema = z.object({
  username: z
    .string()
    .trim()
    .toLowerCase()
    .regex(
      USERNAME,
      '3 to 64 lower-case letters, digits, dots, dashes or underscores, starting with a letter.',
    ),
  email: z.email('That does not look like an email address.'),
  // Normalized and checked by the server, which says which field it refused.
  login_mobile: z.string().trim().max(32).optional(),
  given_name: z.string().trim().min(1, 'A first name, at least.').max(120),
  surname: z.string().trim().max(160).optional(),
  job_title: z.string().trim().max(160).optional(),
  mobile: z.string().trim().max(32).optional(),
});

type FormValues = z.infer<typeof schema>;

interface Provisioned {
  public_id: string;
  username: string;
  email: string;
  full_name: string;
  one_time_password: string;
}

const DEFAULTS: FormValues = {
  username: '',
  email: '',
  login_mobile: '',
  given_name: '',
  surname: '',
  job_title: '',
  mobile: '',
};

export function NewUserDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (publicId: string) => void;
}) {
  const queryClient = useQueryClient();
  const [department, setDepartment] = useState<string | null>(null);
  const [roles, setRoles] = useState<Assignment[]>([]);
  const [provisioned, setProvisioned] = useState<Provisioned | null>(null);
  const [copied, setCopied] = useState(false);

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) return;
    form.reset(DEFAULTS);
    setDepartment(null);
    setRoles([]);
    setProvisioned(null);
    setCopied(false);
  }, [open, form]);

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<Provisioned>('/iam/users', {
        username: values.username,
        email: values.email,
        login_mobile: values.login_mobile?.trim() || null,
        given_name: values.given_name,
        surname: values.surname?.trim() || '',
        job_title: values.job_title?.trim() || null,
        mobile: values.mobile?.trim() || null,
        department_id: department,
        is_superuser: false,
        roles: roles.map((r) => ({
          role_id: r.role_id,
          scope_kind: r.scope_kind,
          department_id: r.department_id,
          team_id: r.team_id,
        })),
      }),
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['iam'] });
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      setProvisioned(record);
      onCreated?.(record.public_id);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        // "already the username of another account" — a refusal about the
        // whole request, shown whole.
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const submit = () => void form.handleSubmit((values) => create.mutateAsync(values))();

  // ── the one-time password panel ───────────────────────────
  if (provisioned) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Account created"
        size="md"
        // Dismissing by accident would take the only copy of the password with
        // it, so the backdrop does not close this.
        dismissOnBackdrop={false}
        footer={
          <Button size="sm" onClick={onClose}>
            I have passed it on
          </Button>
        }
      >
        <div className="space-y-4">
          <Alert tone="warning" title="This password is shown once">
            It is not stored anywhere and there is no way to see it again. If it is lost, reset
            the account — which issues a different one.
          </Alert>

          <div className="space-y-1">
            <p className="text-xs font-medium text-muted">{provisioned.full_name}</p>
            <p className="text-sm text-body">
              Signs in as {provisioned.username} or {provisioned.email}
            </p>
          </div>

          <div className="flex items-center gap-2 rounded-control border border-line bg-surface-2 p-2.5">
            <KeyRound className="size-4 shrink-0 text-faint" aria-hidden />
            <code className="min-w-0 flex-1 truncate font-mono text-sm text-body">
              {provisioned.one_time_password}
            </code>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(provisioned.one_time_password);
                  setCopied(true);
                  toast.success('Copied');
                } catch {
                  // A clipboard permission refusal is not worth a failure
                  // dialog: the password is on screen and can be read.
                  toast.error('Could not copy', 'Select it and copy it by hand.');
                }
              }}
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>

          <p className="text-2xs leading-relaxed text-faint">
            They will be asked to choose their own password the first time they sign in, so this
            one has a short life by design.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New staff account"
      description="Creates a login for a colleague. The password is generated and shown once."
      size="lg"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button size="sm" loading={create.isPending} onClick={submit}>
            Create account
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="First name"
            required
            error={form.formState.errors.given_name?.message}
            {...form.register('given_name')}
          />
          <Input
            label="Surname"
            error={form.formState.errors.surname?.message}
            {...form.register('surname')}
          />
          <Input
            label="Username"
            required
            autoCapitalize="none"
            spellCheck={false}
            placeholder="sam.patel"
            hint="Signs in."
            error={form.formState.errors.username?.message}
            {...form.register('username')}
          />
          <Input
            label="Email"
            type="email"
            required
            hint="Signs in too, and is where a reset goes."
            error={form.formState.errors.email?.message}
            {...form.register('email')}
          />
          <Input
            label="Sign-in mobile"
            type="tel"
            inputMode="tel"
            placeholder="Optional"
            hint="Signs in too, and receives sign-in codes."
            error={form.formState.errors.login_mobile?.message}
            {...form.register('login_mobile')}
          />
          <Input
            label="Job title"
            error={form.formState.errors.job_title?.message}
            {...form.register('job_title')}
          />
          <Input
            label="Contact mobile"
            hint="On the people register. Never signs in."
            error={form.formState.errors.mobile?.message}
            {...form.register('mobile')}
          />
        </div>

        <RecordSelect
          label="Department"
          endpoint="/hr/departments"
          value={department}
          onChange={(next) => setDepartment(next)}
          toOption={(row) => ({
            value: String(row.public_id),
            label: String(row.name),
            detail: String(row.parent_name ?? ''),
          })}
          placeholder="No department"
          hint="Needs the People service. A department-scoped role is measured against this."
        />

        <RoleAssignmentEditor
          value={roles}
          onChange={setRoles}
          hint="An account with no role can sign in and do nothing. That is a valid state, and almost never the one you want."
        />
      </form>
    </Modal>
  );
}
